import { useState, useEffect } from 'react';
import {
  useParams,
  useNavigate,
  useLocation,
  generatePath,
} from 'react-router-dom';
import {
  SidePanel,
  ButtonPseudo,
  SidePanelLoader,
  SidePanelType,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import Form from './agreement-form';
import { ApplicationType } from '../types';
import {
  useLazyGetApplicationAgreementQuery,
  useLazyGetCustomersAgreementFileQuery,
} from '../store/services';
import { clearApplicationAgreementState } from '../store/slices';
import getAgreementTypeName from '../helpers/get-agreement-type-name';

import ROUTES from '../../../routes';

import cn from '../new-application.module.css';

const ApplicationAgreement = ({
  applicationType,
}: {
  applicationType: ApplicationType;
}) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [link, setLink] = useState('');

  const { agreement } = useAppSelector((state) => state.newApplications.list);

  const [getAgreement, { isLoading, isFetching }] =
    useLazyGetApplicationAgreementQuery();
  const [getFile] = useLazyGetCustomersAgreementFileQuery();

  const { agreementId, applicationId } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();

  const getAgreementFile = async (id: string) => {
    const response = await getFile(id);

    if (response.data) {
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      setLink(blob);
    }
  };

  const dispatch = useAppDispatch();

  const handleClose = () => {
    setPanelIsOpen(false);

    let applicationLink = '';

    if (applicationType === ApplicationType.INDIVIDUAL) {
      applicationLink = ROUTES.CUSTOMERS_NEW_APPLICATION_INDIVIDUAL_INFO;
    } else if (applicationType === ApplicationType.CORPORATE) {
      applicationLink = ROUTES.CUSTOMERS_NEW_APPLICATION_CORPORATE_INFO;
    } else if (applicationType === ApplicationType.COMPANY_OFFICIAL) {
      applicationLink = ROUTES.CUSTOMERS_NEW_APPLICATION_COMPANY_OFFICIAL;
    }

    setTimeout(() => {
      navigate(
        `${generatePath(applicationLink, {
          applicationId: (applicationId as string) || '',
        })}${search || ''}`,
      );
    }, 100);
  };

  useEffect(() => {
    setPanelIsOpen(true);

    if (agreementId) {
      getAgreement(agreementId);
      getAgreementFile(agreementId);
    }

    return () => {
      dispatch(clearApplicationAgreementState());
    };
  }, [agreementId]);

  let content;

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else {
    content = (
      <>
        {agreement?.uploadId && (
          <div className={cn.file}>
            <div
              className={`${cn.fileName} ${
                agreementId ? cn.fileNameFullWidth : ''
              }`}
            >
              {agreement && link && (
                <a
                  href={link}
                  target="_blank"
                  rel="noreferrer"
                >{`${getAgreementTypeName(
                  agreement?.type,
                )} (v${agreement.version})`}</a>
              )}
              {!link && 'Loading...'}
            </div>
          </div>
        )}
        <div className={cn.agreementFormSeparator} />
        <Form agreement={agreement} agreementId={agreementId} />
      </>
    );
  }

  let title;

  if (agreement) {
    title = getAgreementTypeName(agreement?.type);
  } else {
    title = 'Agreement';
  }

  return (
    <SidePanel
      type={SidePanelType.AML}
      closeOnClickOutside
      isOpen={panelIsOpen}
      onClose={handleClose}
      title={title}
      footer={<ButtonPseudo text="Close" onClick={handleClose} bold />}
      sidepanelExpandedKey="application-agreement"
    >
      {content}
    </SidePanel>
  );
};

export default ApplicationAgreement;
